import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isEqual, isNil, isUndefined } from 'lodash-es';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  authenticationLoginEmail,
  authenticationRegisterPassword,
  logout,
  updateUser,
} from '@/store/slices/authentication';
import { sessionEnsure, updateSessionPostAuth } from '@/store/slices/session';
import { isEmailValid } from '@/utils/strings';
import { resolvePostAuthRoute } from '@/utils/post-auth-route';
import { resolveLoginRedirect } from '@/utils/login-redirect';
import { resolveConfiguratorFlowBase } from '@/utils/configurator-flow';
import { legalUrl } from '@/nova/config/websiteUrls';
import PageLoader from '@/components/page-loader';

import { Text } from '../../components/Text';
import { Divider } from '../../components/Divider';
import { useLanguageSwitcher } from '../../hooks/useLanguageSwitcher';
import { AuthLogo } from '../login/components/AuthLogo';
import { CtaHint, CTA_HINT_ID } from '../login/components/CtaHint';
import { LanguagePicker } from '../login/components/LanguagePicker';
import { SocialLoginBar } from '../social-login/components/SocialLoginBar';
import { useAuthCardMorph } from '../login/useAuthCardMorph';
import { useAuthInputFocus } from '../login/useAuthInputFocus';
import { scorePassword } from '../../utils/password-strength';
import { Register } from './register.styles';

const MAIL_SENT_SRC = 'https://micros-t.b-cdn.net/owners-hub-register-mail-sent-compact-1777293313810.svg';

type RegisterLayout = 'form' | 'next';

// Which sign-up method the shared form is showing. Currently forced to `magic` because password
// register has no BE endpoint (see PASSWORD_REGISTER_ENABLED below). NOTE the native gap this
// re-exposes: there is no inbound-email/deep-link return for a magic link on native, and the app
// stores require a username + password path, so native register has no functional flow until BE
// ships register-with-password and this is re-enabled.
type RegisterMode = 'magic' | 'password';

// Password register is DISABLED: the shipped micros-wl_owner_hub backend has no
// register-with-password endpoint (`/auth/register` accepts no password). Until BE ships one
// (SPB-352), the form is magic-link only and the mode toggle is hidden. Flip this to `true` to
// re-enable the whole (retained) password path in one line. See `authenticationRegisterPassword`.
const PASSWORD_REGISTER_ENABLED = false;

/**
 * `/register`: sign-up in two modes, mirroring `/login`.
 *
 * Hand-written, strictly-typed reproduction of the config-driven
 * `module.owners.hub.register` (`src/components/modules/register/index.tsx`). It CAN offer the
 * same two modes as `/login`, but Password mode is currently DISABLED (see
 * `PASSWORD_REGISTER_ENABLED` above): the shipped backend has no register-with-password endpoint,
 * so the page ships magic-link-only with the mode toggle hidden. The two modes, once re-enabled:
 * - **Magic link** (the only mode shown today): enter an email (with a marketing-consent
 *   checkbox), request a secure link, then swap to the "email sent" view; the actual sign-in
 *   completes on `/login?link=…` when the user returns. This flow is unchanged.
 * - **Password** (DISABLED pending BE; on the app stores it is the required username+password
 *   path): enter an email + a required password and submit to CREATE the account AND sign in
 *   immediately (a real auth event, {@link authenticationRegisterPassword}), then route on with
 *   {@link resolvePostAuthRoute} exactly like `/login`'s password sign-in.
 *
 * The visual tree mirrors the template node-for-node (styles in `register.styles.ts`), and the
 * language switcher reuses the same `LanguagePicker` as `/login`. Rendered full-bleed OUTSIDE the
 * account chrome (see `pages/register/router.tsx`). It is the screen the auth gate sends an
 * unauthenticated visitor to.
 */
const PageRegister = (): React.ReactElement => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const { languages, currentLanguage, onChangeLanguage } = useLanguageSwitcher();
  const { innerRef, cardStyle } = useAuthCardMorph();

  const authentication = useAppSelector((state) => state.authentication, isEqual);
  const casinoInstance = useAppSelector((state) => state.casinoInstance, isEqual);
  const session = useAppSelector((state) => state.session, isEqual);

  const [layout, setLayout] = React.useState<RegisterLayout>('form');
  const [email, setEmail] = React.useState<string | undefined>();
  // Sign-up method shown by the shared form. Forced to `magic` while password register is disabled
  // (PASSWORD_REGISTER_ENABLED, no BE endpoint); the toggle that would let the user switch is also
  // hidden. The whole password path below is retained but unreached. Matches `/login` once re-enabled.
  const [mode, setMode] = React.useState<RegisterMode>(PASSWORD_REGISTER_ENABLED ? 'password' : 'magic');
  // Password-mode field + its show/hide affordance. Shared with neither the email field nor the
  // magic-link flow.
  const [password, setPassword] = React.useState<string>('');
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  // True once a password register has failed: drives the single "we could not create your account"
  // notice. Cleared when the owner edits a field or switches mode, and reset before each submit.
  const [passwordError, setPasswordError] = React.useState<boolean>(false);
  // True once THIS password register has fully resolved (account created, token set, session +
  // brands loaded — the thunk awaits all three). The routing effect below waits on it, exactly
  // like `/login`'s `passwordResolved` does for its password sign-in.
  const [registerResolved, setRegisterResolved] = React.useState<boolean>(false);
  // True once an SSO (Google) sign-in has settled here (token + session + brands). Opens the same
  // routing gate as a password register, mirroring `/login`'s `ssoResolved`. Telegram routes via its
  // own /telegram-callback page, so only the in-place Google flow flips this.
  const [ssoResolved, setSsoResolved] = React.useState<boolean>(false);
  const [termsAgreed, setTermsAgreed] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  // Autofocuses the email field on show but suppresses the browser autofill popup
  // on that programmatic focus (see useAuthInputFocus); spread inputProps on the input.
  const { emailRef, inputProps } = useAuthInputFocus(layout === 'form' && !loading);
  // Where the auth service aimed THIS register (`redirect_url` on the response), already resolved
  // to an in-app path. Held in a ref rather than state because it is written in the same tick as
  // `setRegisterResolved`, so the routing effect below reads it on the very render that opens its
  // gate, and because it must not itself re-trigger that effect (mirrors `/login`).
  const redirectTargetRef = React.useRef<string | null>(null);

  // Magic-link post-authentication handler: "mail" → show the email-sent view; "redirect" → route
  // on. Only the magic flow sets `session.postAuth` (via authenticationLoginEmail), so this effect
  // never fires for the password flow, which routes through its own effect below (same split as
  // `/login`).
  React.useEffect(() => {
    const nextPage = resolvePostAuthRoute({
      sessionStatus: session.status,
      brandsLoaded: casinoInstance.loaded,
      brandsCount: casinoInstance.brands.length,
      configuratorBase: resolveConfiguratorFlowBase(),
    });

    switch (session.postAuth) {
      case 'mail':
        setLayout('next');
        break;
      case 'redirect':
        setLoading(true);
        navigate(nextPage);
        break;
      default:
        break;
    }
  }, [
    navigate,
    session.postAuth,
    session.status,
    casinoInstance.brands.length,
    casinoInstance.loaded,
    casinoInstance.loading,
  ]);

  // Password register success → route on, once the register flow has resolved AND the session it
  // loaded has settled. Mirrors `/login`'s password routing gate: `authenticationRegisterPassword`
  // sets the token and awaits userBrandsList + sessionLoad before fulfilling, so `registerResolved`
  // flips only when all three are ready. A `redirect_url` on the response wins over the brand-state
  // default (held in the ref).
  React.useEffect(() => {
    const authCompleted = !authentication.in_progress && authentication.auth_type === 'user';
    const casinoCompleted = !casinoInstance.loading && casinoInstance.loaded;
    // A password register OR an in-place Google SSO sign-in opens this gate; both set their flag only
    // after the token is set and the session + brands have loaded (see `/login`'s unified gate).
    const authenticatedSessionReady = (registerResolved || ssoResolved) && session.loaded && !session.loading;

    if (authCompleted && casinoCompleted && authenticatedSessionReady) {
      setLoading(true);
      navigate(
        redirectTargetRef.current ??
          resolvePostAuthRoute({
            sessionStatus: session.status,
            brandsLoaded: casinoInstance.loaded,
            brandsCount: casinoInstance.brands.length,
            configuratorBase: resolveConfiguratorFlowBase(),
          }),
      );
    }
  }, [authentication, casinoInstance, session, navigate, registerResolved, ssoResolved]);

  const handleChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setEmail(event.target.value);
    // The notice referred to the submit they are now editing, so drop it as they type.
    setPasswordError(false);
  }, []);

  const handlePasswordChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setPassword(event.target.value);
    setPasswordError(false);
  }, []);

  const handleCheck = React.useCallback(() => {
    setTermsAgreed((prev) => !prev);
  }, []);

  const onLabelKeyDown = React.useCallback(
    (event: React.KeyboardEvent): void => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleCheck();
      }
    },
    [handleCheck],
  );

  // Switch sign-up method. Clears the failure notice (it referred to the method left behind) and
  // deliberately leaves the shared email value untouched (mirrors `/login`).
  const handleModeChange = React.useCallback((next: RegisterMode) => {
    setMode(next);
    setPasswordError(false);
  }, []);

  // Validation mirrors the template's `isValid`: a well-formed email. (The consent checkbox is
  // captured but, as in the source module, does not gate submit, see the conversion note below.)
  const isValid = React.useMemo<boolean>(() => isEmailValid(email), [email]);

  // Password-mode enable gate (the password is REQUIRED, min 8 chars). The handler self-gates on
  // the same predicate (react rule: guards live in handlers, not only in styling).
  const canSubmitPassword = isValid && password.length >= 8;

  // Strength meter state, derived from the password (never mirrored via effect). The meter block
  // is always rendered in password mode with a reserved height, so it never shifts the layout as
  // the user types; only the fill and caption change.
  const strength = React.useMemo(() => scorePassword(password), [password]);

  // Guards a double-click from racing two mints while the first submit is still ensuring
  // a session (that request is async now that minting is deferred to submit).
  const submittingRef = React.useRef(false);

  const handleSubmit = React.useCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => {
      event.preventDefault();

      if (isUndefined(email) || !isValid || submittingRef.current) {
        return;
      }

      submittingRef.current = true;

      // This page is NOT auth-gated, so an already-signed-in owner can reach it (browser back, or
      // typing the URL) and submit a DIFFERENT email. That is the case the sign-out below exists
      // for, and the reason it is unconditional.
      //
      // Sign the CURRENT identity out before the mint below, or the session is created owned by
      // the PREVIOUS owner (the mint sends no token of its own, so `sessionLoad` falls back to
      // whatever is in the store, and the backend takes ownership from that header) and the
      // emailed link then fails with an owner mismatch on redemption. Scoped so the marketing
      // hand-off and the language pick survive: both are read moments later, by the mint and by
      // the magic-link email. Full rationale and its accepted trade-offs live in
      // `store/visitor-context-teardown.ts`. Must precede `updateUser`, which logout would clear.
      dispatch(logout({ keepVisitorContext: true }));

      dispatch(updateUser({ email }));

      // Mint-then-send, sequentially. The magic-link request REQUIRES a session_id to bind
      // the sign-up to, so ensure a session exists FIRST, then request the link with the
      // resulting id. Minting is deferred to here (was on mount) so a session is created ONLY
      // once we have an email: bounced/abandoned visits no longer leave useless email-less
      // rows in the DB, and the typed email is stamped onto the fresh mint so the session is
      // never even momentarily email-less. Waiting on the mint here is not a critical path.
      // `fresh: true` MINTS for the typed email and never resumes a stale/foreign session id left
      // in this browser's localStorage (which would load the wrong session and kill sign-up); the
      // backend reconciles the real session by email once the owner authenticates.
      void dispatch(sessionEnsure({ email, fresh: true }))
        .unwrap()
        .then((result) => {
          if (!isNil(result.id)) {
            dispatch(authenticationLoginEmail({ email, sessionId: result.id }));
          }
        })
        .catch(() => {
          // Ensure/mint failed (e.g. network). Leave the form interactive so the user can retry.
        })
        .finally(() => {
          submittingRef.current = false;
        });
    },
    [dispatch, email, isValid],
  );

  // Password register. Unlike magic-link sign-up this authenticates immediately: mint a session
  // (preserving the visitor-context / marketing hand-off, exactly like the magic flow), then
  // `authenticationRegisterPassword` creates the account, sets the token and awaits the session +
  // brands load, so we only mark the flow resolved and let the routing effect above route on with
  // any `redirect_url` the response carried. ANY failure (mint, or the 404 register endpoint until
  // BE ships) surfaces the single notice and leaves the form interactive, with no partial sign-up.
  const handlePasswordSubmit = React.useCallback<React.FormEventHandler<HTMLFormElement>>(
    (event) => {
      event.preventDefault();

      if (isUndefined(email) || !isValid || password.length < 8 || submittingRef.current) {
        return;
      }

      submittingRef.current = true;
      setPasswordError(false);
      setLoading(true);

      // Same unconditional sign-out + email stamp as the magic flow, for the same reason (an
      // already-signed-in owner registering a different email must not mint under the old identity).
      dispatch(logout({ keepVisitorContext: true }));
      dispatch(updateUser({ email }));

      void dispatch(sessionEnsure({ email, fresh: true }))
        .unwrap()
        .then(async (result) => {
          if (isNil(result.id)) {
            throw new Error('session mint returned no id');
          }

          const authResult = await dispatch(
            authenticationRegisterPassword({ email, password, sessionId: result.id }),
          ).unwrap();

          // The auth service decides where a fresh register lands (same as a redeemed link);
          // keep it for the routing effect above (absent/unusable resolves to null → default).
          redirectTargetRef.current = resolveLoginRedirect(authResult.data.redirect_url);
          setRegisterResolved(true);
        })
        .catch(() => {
          // Mint or register rejected (the register endpoint is 404 until BE ships). Drop the
          // spinner, surface the single notice, and leave the form interactive so the user can
          // retry. No account is signed in on a failure.
          setLoading(false);
          setPasswordError(true);
        })
        .finally(() => {
          submittingRef.current = false;
        });
    },
    [dispatch, email, isValid, password],
  );

  // Back from the "email sent" view to the sign-up form. postAuth is a one-shot
  // 'mail' signal that drove us here; clear it so the post-auth effect doesn't
  // immediately snap the layout back to 'next' on the next session update.
  const handleBackToForm = React.useCallback(() => {
    dispatch(updateSessionPostAuth(null));
    setLayout('form');
  }, [dispatch]);

  if (loading) {
    return <PageLoader />;
  }

  // Root class is `nova-register`, NOT `register`: the config-driven register module's
  // global stylesheet is scoped `.register .page .main .body { … }` and our DOM mirrors
  // that `.page > .main > .body` structure, so reusing `.register` here let those
  // higher-specificity rules (height/padding/justify-content) win over our own and leak
  // in. A non-colliding root class keeps this page fully self-styled.
  return (
    <Register className="nova-register">
      <div className="page">
        <div className="main">
          <div className="header">
            <AuthLogo />
          </div>

          <div className="body" style={cardStyle}>
            <div className="body-inner" ref={innerRef}>
              <div className="layout">
                {layout === 'form' ? (
                  <div className="wrapper form-view">
                    <form
                      className="sign-in-form"
                      onSubmit={mode === 'password' ? handlePasswordSubmit : handleSubmit}
                    >
                      <div className="heading">
                        <Text className="title" text="Start building your iGaming website" />
                        <Text
                          className="subtitle"
                          text="Enter your email to begin. Your progress is saved automatically."
                        />
                      </div>

                      {/* Password first, Magic link second, in that fixed order on every platform
                        (matches our other site). Hidden while password register is disabled
                        (PASSWORD_REGISTER_ENABLED) so only the magic-link flow shows; the buttons
                        stay in source (labels remain extracted, not orphaned) for a one-flag revert. */}
                      {PASSWORD_REGISTER_ENABLED ? (
                        <div className="mode-toggle" role="group">
                          <button
                            type="button"
                            className={`mode-option${mode === 'password' ? ' active' : ''}`}
                            onClick={() => handleModeChange('password')}
                          >
                            <Text text="Password" />
                          </button>
                          <button
                            type="button"
                            className={`mode-option${mode === 'magic' ? ' active' : ''}`}
                            onClick={() => handleModeChange('magic')}
                          >
                            <Text text="Magic link" />
                          </button>
                        </div>
                      ) : null}

                      {passwordError ? (
                        <div className="link-notice" role="alert">
                          <i className="bi bi-exclamation-circle-fill notice-icon" aria-hidden="true" />
                          <div className="notice-copy">
                            <Text className="notice-title" text="We could not create your account" />
                            <Text
                              className="notice-hint"
                              text="Please check your details and try again."
                            />
                          </div>
                        </div>
                      ) : null}

                      <div className="input">
                        <input
                          ref={emailRef}
                          type="email"
                          name="email"
                          autoComplete="email"
                          placeholder={t('Enter your email to begin')}
                          value={email ?? ''}
                          onChange={handleChange}
                          {...inputProps}
                        />

                        {mode === 'password' ? (
                          <>
                            <div className="password-input">
                              <input
                                type={showPassword ? 'text' : 'password'}
                                name="new-password"
                                autoComplete="new-password"
                                placeholder={t('Create a password')}
                                value={password}
                                onChange={handlePasswordChange}
                                maxLength={128}
                              />
                              <button
                                type="button"
                                className="eye-toggle"
                                aria-label={t(showPassword ? 'Hide password' : 'Show password')}
                                onClick={() => setShowPassword((v) => !v)}
                              >
                                <i
                                  className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}
                                  aria-hidden="true"
                                />
                              </button>
                            </div>

                            {/* Strength meter + caption. The block keeps a fixed reserved height
                              at all times (so the form never reflows), but stays visually hidden
                              until the first keystroke (`is-visible`), then fades in. Bars fill as
                              a left-to-right gradient by score; colours + reveal live in the
                              styles. Bars are decorative (aria-hidden); the caption carries the
                              meaning, and while hidden `visibility:hidden` keeps it out of the a11y
                              tree so nothing is announced for an empty field. */}
                            <div
                              className={`password-strength strength-${strength.level}${
                                password.length > 0 ? ' is-visible' : ''
                              }`}
                            >
                              <div className="strength-bars" aria-hidden="true">
                                <span className={`bar${strength.score >= 1 ? ' filled' : ''}`} />
                                <span className={`bar${strength.score >= 2 ? ' filled' : ''}`} />
                                <span className={`bar${strength.score >= 3 ? ' filled' : ''}`} />
                              </div>
                              <span className="strength-caption" aria-live="polite">
                                {password.length === 0 ? null : strength.level === 'too-short' ? (
                                  <Text text="Use at least 8 characters." />
                                ) : strength.level === 'weak' ? (
                                  <Text text="Weak password" />
                                ) : strength.level === 'fair' ? (
                                  <Text text="Fair password" />
                                ) : (
                                  <Text text="Strong password" />
                                )}
                              </span>
                            </div>
                          </>
                        ) : null}

                        <div className="checkbox">
                          <input type="checkbox" checked={termsAgreed} onChange={handleCheck} />
                          <div className="label">
                            {/* One <Text> = one i18n key (reorderable per locale). The template
                              bolded "marketing communications" via RichText's dangerouslySetInnerHTML,
                              which this area forbids; with no <Trans> precedent in the repo, the
                              area-correct form is a single key without the inline emphasis. */}
                            <span
                              className="text"
                              role="button"
                              tabIndex={0}
                              onClick={handleCheck}
                              onKeyDown={onLabelKeyDown}
                            >
                              <Text text="I agree to receive marketing communications about Whitelabels.com products, promotions and updates." />
                            </span>
                            <span className="link">
                              {/* The public Privacy Policy lives on whitelabels.com, locale-aware and
                                identical to the Settings › Legal link (see `@/nova/config/websiteUrls`).
                                An external site + unsaved form state → open in a new tab. */}
                              <a
                                href={legalUrl(i18n.language, 'privacy-policy')}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Text text="Privacy Policy." />
                              </a>
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="cta">
                        {mode === 'password' ? (
                          <button
                            type="submit"
                            className={`button${canSubmitPassword ? '' : ' disabled'}`}
                            disabled={!canSubmitPassword}
                          >
                            <Text className="text" text="Create account" />
                            <i className="bi bi-arrow-right icon" aria-hidden="true" />
                          </button>
                        ) : (
                          <CtaHint email={email} disabled={!isValid}>
                            <button
                              type="submit"
                              className={`button${isValid ? '' : ' disabled'}`}
                              disabled={!isValid}
                              aria-describedby={isValid ? undefined : CTA_HINT_ID}
                            >
                              <Text className="text" text="Continue" />
                              <i className="bi bi-arrow-right icon" aria-hidden="true" />
                            </button>
                          </CtaHint>
                        )}
                        <Text className="note" text="No credit card required. Free to explore." />
                      </div>
                    </form>

                    <SocialLoginBar
                      redirectTargetRef={redirectTargetRef}
                      onResolved={() => setSsoResolved(true)}
                      onBusyChange={setLoading}
                      mode="signup"
                    />

                    <Divider variant="styled" className="auth-divider" />

                    <div className="footer">
                      <Text className="label" text="Already have an account?" />
                      <Link className="link" to="/login">
                        <Text text="Sign in." />
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="wrapper sent-view">
                    <div className="heading">
                      <button
                        type="button"
                        className="back"
                        onClick={handleBackToForm}
                        aria-label={t('Back')}
                      >
                        <i className="bi bi-arrow-left" aria-hidden="true" />
                      </button>

                      <div className="heading-copy">
                        <Text className="title" text="An email is on the way" />
                        <span className="subtitle">
                          <Text text="We have sent you a secure login link to your email address." />{' '}
                          <Text text="It will log you in if you have a Whitelabels.com account." />
                        </span>
                      </div>
                    </div>

                    <div className="email-chip">
                      <i className="bi bi-envelope icon" aria-hidden="true" />
                      <Text className="email" text={email ?? ''} />
                    </div>

                    <div className="art">
                      <img className="mail-sent" src={MAIL_SENT_SRC} alt="" />
                    </div>

                    <Divider variant="styled" className="auth-divider" />

                    <Text className="warning" text="The link expires in 15 minutes." />
                  </div>
                )}
              </div>
            </div>
          </div>

          <LanguagePicker languages={languages} currentLanguage={currentLanguage} onChange={onChangeLanguage} />
        </div>
      </div>
    </Register>
  );
};

export default PageRegister;
